import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { ExerciseHistory } from '@/lib/types';

export function useWorkoutPerformance(studentId: string) {
  const [history, setHistory] = useState<ExerciseHistory[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (studentId) fetch();
    else setLoading(false);
  }, [studentId]);

  async function fetch() {
    setLoading(true);

    const { data: sessions } = await supabase
      .from('workout_sessions')
      .select('id, created_at')
      .eq('student_id', studentId)
      .not('completed_at', 'is', null)
      .order('created_at', { ascending: true });

    if (!sessions?.length) { setHistory([]); setLoading(false); return; }

    const sessionIds = sessions.map(s => s.id);
    const sessionDateMap: Record<string, string> = {};
    sessions.forEach(s => { sessionDateMap[s.id] = s.created_at; });

    const { data: logs } = await supabase
      .from('session_logs')
      .select(`
        plan_exercise_id,
        load_used,
        session_id,
        plan_exercises!inner(
          exercise_id,
          exercises!inner(id, name, muscle_group)
        )
      `)
      .in('session_id', sessionIds);

    if (!logs?.length) { setHistory([]); setLoading(false); return; }

    const exerciseMap: Record<string, { name: string; muscleGroup: string; entries: { date: string; load: number | null }[] }> = {};

    logs.forEach((log: any) => {
      const exercise = log.plan_exercises?.exercises;
      if (!exercise) return;
      const exerciseId = log.plan_exercises.exercise_id;
      if (!exerciseMap[exerciseId]) {
        exerciseMap[exerciseId] = { name: exercise.name, muscleGroup: exercise.muscle_group, entries: [] };
      }
      exerciseMap[exerciseId].entries.push({ date: sessionDateMap[log.session_id], load: log.load_used ?? null });
    });

    const result: ExerciseHistory[] = Object.entries(exerciseMap).map(([exerciseId, data]) => {
      const sorted = [...data.entries].sort((a, b) => a.date.localeCompare(b.date));
      const withLoad = sorted.filter(e => e.load !== null);
      const firstLoad = withLoad[0]?.load ?? null;
      const lastLoad = withLoad[withLoad.length - 1]?.load ?? null;
      const delta = firstLoad !== null && lastLoad !== null && withLoad.length > 1 ? lastLoad - firstLoad : null;
      return {
        exerciseId,
        name: data.name,
        muscleGroup: data.muscleGroup,
        entries: sorted,
        firstLoad,
        lastLoad,
        delta,
        lastPerformedAt: sorted[sorted.length - 1]?.date ?? '',
      };
    });

    result.sort((a, b) => b.lastPerformedAt.localeCompare(a.lastPerformedAt));
    setHistory(result);
    setLoading(false);
  }

  return { history, loading };
}
